import { useState } from "react";
import { toast } from "sonner";
import { UploadCloud, AlertCircle, AlertTriangle, CheckCircle2, Download, FileDown } from "lucide-react";

import { api, API_BASE, apiErrorMessage, downloadAuthenticatedFile } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const COMMIT_CHUNK_SIZE = 150;

const EXPECTED_COLUMNS = [
  "Student Roll No", "Full Name as per MU Records", "Gender", "School",
  "Mobile Number 1", "Mobile Number 2", "Residential Address",
  "Attending Convocation", "No. of Guests", "Guest 1 Details", "Guest 2 Details",
  "Hostel Required", "Arrival Date/Time", "Departure Date/Time",
  "Farewell Dinner", "Farewell Transport Needed",
];

export default function UploadRegistrations() {
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [committed, setCommitted] = useState(false);
  const [commitProgress, setCommitProgress] = useState(null); // { done, total, inserted }

  const onValidate = async () => {
    if (!file) { toast.error("Choose an Excel file first"); return; }
    setBusy(true); setReport(null); setCommitted(false);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload/validate", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReport(r.data);
      toast.success(`Validated: ${r.data.valid_count} valid, ${r.data.invalid_count} invalid`);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally { setBusy(false); }
  };

  const onCommit = async () => {
    if (!report || report.valid_count === 0) return;
    setBusy(true);
    const rows = report.valid_rows;
    const chunks = [];
    for (let i = 0; i < rows.length; i += COMMIT_CHUNK_SIZE) chunks.push(rows.slice(i, i + COMMIT_CHUNK_SIZE));

    let totalInserted = 0;
    setCommitProgress({ done: 0, total: chunks.length, inserted: 0 });
    try {
      for (let i = 0; i < chunks.length; i++) {
        const r = await api.post("/upload/commit", { rows: chunks[i] });
        totalInserted += r.data.inserted;
        setCommitProgress({ done: i + 1, total: chunks.length, inserted: totalInserted });
      }
      toast.success(`Inserted ${totalInserted} registration(s)`);
      setCommitted(true);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
      setCommitProgress(null);
    }
  };

  const downloadErrorReport = () => {
    if (!report) return;
    const headers = ["row_number", "roll_number", "errors"];
    const lines = [headers.join(",")].concat(
      report.invalid_rows.map((r) => [r.row_number, r.htno, `"${(r.errors || []).join("; ")}"`].join(","))
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "validation_errors.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadTemplate = async () => {
    try {
      await downloadAuthenticatedFile("/upload/template", "registrations_upload_template.xlsx");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  return (
    <div data-testid="upload-page">
      <PageHeader
        title="Upload Registrations"
        subtitle="Upload an Excel file (.xlsx). Rows are validated first; nothing is inserted until you commit."
        actions={
          <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
            <FileDown size={14} className="mr-2" /> Download Template
          </Button>
        }
      />

      <div className="bg-white border border-border rounded-xl p-8 mb-6">
        <p className="kicker mb-3">Step 1 — Select file</p>
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
          <label className="inline-flex items-center gap-3 border border-dashed border-slate-300 rounded-md px-4 py-3 cursor-pointer hover:border-primary transition-colors">
            <UploadCloud size={18} />
            <span className="text-sm">{file ? file.name : "Choose .xlsx file"}</span>
            <input
              data-testid="upload-file-input"
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </label>
          <Button
            data-testid="upload-validate-btn"
            className="bg-primary hover:bg-primary/90"
            onClick={onValidate}
            disabled={!file || busy}
          >
            Validate Upload
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-3">
          Expected columns: <code className="bg-slate-100 px-1.5 py-0.5 rounded break-all">{EXPECTED_COLUMNS.join(", ")}</code>
        </p>
        <p className="text-xs text-slate-500 mt-2">
          <b>Student Roll No</b> is required and must match an existing student. Guest 1 / Guest 2 Details can be one combined text field (e.g. "Karuna Chary - Father - 9000088111") — it's parsed automatically; rows where it can't be parsed confidently are flagged as a warning for you to review, not blocked.
        </p>
        <p className="text-xs text-slate-500 mt-2">
          Students must be on the <b>Students (Masterlist)</b> tab with fee cleared before they can be registered — upload the masterlist first if you haven't already.
        </p>
      </div>

      {report && (
        <div className="bg-white border border-border rounded-xl p-8" data-testid="validation-report">
          <p className="kicker mb-3">Step 2 — Validation Report</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="border rounded-md p-4">
              <p className="text-xs text-slate-500">Total</p>
              <p className="text-3xl font-serif" data-testid="report-total">{report.total}</p>
            </div>
            <div className="border rounded-md p-4">
              <p className="text-xs text-slate-500">Valid</p>
              <p className="text-3xl font-serif text-emerald-600" data-testid="report-valid">{report.valid_count}</p>
            </div>
            <div className="border rounded-md p-4">
              <p className="text-xs text-slate-500">Invalid</p>
              <p className="text-3xl font-serif text-primary" data-testid="report-invalid">{report.invalid_count}</p>
            </div>
            <div className="border rounded-md p-4">
              <p className="text-xs text-slate-500">Warnings</p>
              <p className="text-3xl font-serif text-amber-500" data-testid="report-warnings">{report.warning_count || 0}</p>
            </div>
          </div>

          {report.warning_count > 0 && (
            <div className="mb-6">
              <p className="kicker flex items-center gap-2 mb-3 text-amber-600"><AlertTriangle size={14} /> Valid, but with warnings</p>
              <div className="overflow-x-auto max-h-64 border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Row</TableHead>
                      <TableHead>Roll No.</TableHead>
                      <TableHead>Warnings</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.valid_rows.filter((r) => r.warnings?.length).map((r, i) => (
                      <TableRow key={i}>
                        <TableCell>{r.row_number}</TableCell>
                        <TableCell className="font-mono text-xs">{r.htno}</TableCell>
                        <TableCell>
                          {r.warnings.map((w, j) => (
                            <Badge key={j} variant="outline" className="mr-1 mb-1 border-amber-400 text-amber-700 bg-amber-50">{w}</Badge>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {report.invalid_rows.length > 0 && (
            <>
              <div className="flex items-center justify-between mb-3">
                <p className="kicker flex items-center gap-2"><AlertCircle size={14} /> Invalid Rows</p>
                <Button variant="outline" size="sm" onClick={downloadErrorReport} data-testid="download-errors-btn">
                  <Download size={14} className="mr-1" /> Download Error Report
                </Button>
              </div>
              <div className="overflow-x-auto max-h-80 border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Row</TableHead>
                      <TableHead>Roll No.</TableHead>
                      <TableHead>Errors</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.invalid_rows.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell>{r.row_number}</TableCell>
                        <TableCell className="font-mono text-xs">{r.htno}</TableCell>
                        <TableCell>
                          {(r.errors || []).map((e, j) => (
                            <Badge key={j} variant="destructive" className="mr-1 mb-1">{e}</Badge>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}

          <div className="mt-6 flex flex-col items-end gap-3">
            <div className="flex items-center justify-end gap-3 w-full">
              {committed ? (
                <p className="flex items-center gap-2 text-emerald-600" data-testid="commit-done">
                  <CheckCircle2 size={16} /> Registrations committed successfully.
                </p>
              ) : (
                <Button
                  data-testid="commit-upload-btn"
                  className="bg-primary hover:bg-primary/90"
                  disabled={report.valid_count === 0 || busy}
                  onClick={onCommit}
                >
                  {commitProgress ? "Committing…" : `Commit Upload (${report.valid_count})`}
                </Button>
              )}
            </div>
            {commitProgress && (
              <div className="w-full" data-testid="commit-progress">
                <Progress value={(commitProgress.done / commitProgress.total) * 100} />
                <p className="text-xs text-slate-500 mt-2 text-right">
                  {commitProgress.inserted} inserted · batch {commitProgress.done} of {commitProgress.total}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
